"use client";

import { useClerk } from "@clerk/nextjs";
import { LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const SUPPORT_EMAIL = "support@cartracked.com";

const TONES = {
  warning: { bg: "bg-amber-50", fg: "text-amber-600" },
  error: { bg: "bg-red-50", fg: "text-red-600" },
} as const;

/**
 * Full-screen stop for the states the app shell can't get past. Every one of
 * them is a dead end for the visitor, so all of them offer a way out: sign out
 * is always available, because otherwise a bad account is stuck on this screen
 * with no way to try a different one.
 */
export const AuthErrorScreen = ({
  tone,
  icon: Icon,
  title,
  email,
  retry = false,
  children,
}: {
  tone: keyof typeof TONES;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  email?: string;
  retry?: boolean;
  children: React.ReactNode;
}) => {
  const { signOut } = useClerk();
  const { bg, fg } = TONES[tone];

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-50 px-6 animate-in fade-in duration-300">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className={cn("rounded-full p-3.5", bg)}>
            <Icon className={cn("h-7 w-7", fg)} />
          </div>

          <h1 className="mt-5 text-lg font-semibold tracking-tight text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {children}
          </p>

          {email && (
            <div className="mt-5 w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400">
                Signed in as
              </div>
              <div className="mt-0.5 break-all font-mono text-xs text-gray-700">
                {email}
              </div>
            </div>
          )}

          <div className="mt-6 flex w-full flex-col gap-2">
            {retry && (
              <Button onClick={() => window.location.reload()}>
                <RefreshCw />
                Try again
              </Button>
            )}
            <Button
              variant={retry ? "ghost" : "outline"}
              onClick={() => signOut({ redirectUrl: "/v2/sign-in" })}
            >
              <LogOut />
              Sign out
            </Button>
          </div>

          <p className="mt-6 border-t border-gray-100 pt-5 text-xs text-gray-500 w-full">
            Need help?{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>

      <div className="mt-6 text-sm text-gray-400">
        Car<span style={{ color: "#00d97e" }}>Tracked</span>
      </div>
    </div>
  );
};

export default AuthErrorScreen;
